import React, { useCallback, useEffect, useState } from 'react';
import {
  deletePartner,
  fetchAllPartners,
  updatePartnerInfo,
} from '../../services/resellerService';

import AddPartnerModal from './AddResellerModal';
import {
  Button,
  Card,
  CardBody,
  Input,
  Pagination,
  Spinner,
} from '@nextui-org/react';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import EditPartnerModal from './EditResellerModal';
import { PartnerInfo } from '../../types';
import PartnerTable from './ResellerTable';
import { useAuth0 } from '@auth0/auth0-react';

const ROWS_PER_PAGE = 15;

const Admin: React.FC = () => {
  const [partners, setPartners] = useState<PartnerInfo[]>([]);
  const [formData, setFormData] = useState<Partial<PartnerInfo>>({});
  const [error, setError] = useState<string | null>(null);
  const [isAddPartnerModalOpen, setAddPartnerModalOpen] = useState(false);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [selectedPartnerId, setSelectedPartnerId] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();

  const fetchPartners = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const data = await fetchAllPartners(token);
      setPartners(data);
    } catch (err) {
      console.error(err);
      setError('Could not fetch partners');
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    const checkAdminRole = async () => {
      if (isAuthenticated && user) {
        const roles = user['https://api.airlock/roles'] || [];
        if (roles.includes('admin')) {
          setIsAdmin(true);
          await fetchPartners();
        } else {
          setError('Access Denied: You do not have permission to view this page.');
        }
        setIsLoading(false);
      }
    };
    checkAdminRole();
  }, [isAuthenticated, user, fetchPartners]);

  // Reset page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner size="lg" color="secondary" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <Card className="bg-red-50 border border-red-200 shadow-none">
        <CardBody className="p-6">
          <p className="text-sm text-red-700">{error || 'Unauthorized access'}</p>
        </CardBody>
      </Card>
    );
  }

  // Get the correct ID from a partner (Convex uses _id)
  const getPartnerId = (partner: PartnerInfo): string => {
    return (partner as any)._id || partner.id;
  };

  const createAuth0User = async (data: Partial<PartnerInfo>): Promise<string> => {
    const token = await getAccessTokenSilently();
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/partner/create-auth0-user`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          email: data.email,
          first_name: data.first_name,
          last_name: data.last_name,
          company_name: data.company_name,
          password: data.password,
          shopify_customer_id: data.shopify_customer_id,
        }),
      }
    );

    if (!response.ok) {
      const errText = await response.text();
      throw new Error(`Failed to create Auth0 user: ${errText}`);
    }

    const result = await response.json();
    return result.user_id;
  };

  const handleSaveClick = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      if (selectedPartnerId) {
        await updatePartnerInfo(token, { id: selectedPartnerId, ...formData });
        await fetchPartners();
      }
      setEditModalOpen(false);
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'Could not save changes');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteClick = (partner: PartnerInfo) => {
    setSelectedPartnerId(getPartnerId(partner));
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedPartnerId) return;
    setIsDeleting(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      await deletePartner(token, selectedPartnerId);
      await fetchPartners();
      setDeleteModalOpen(false);
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'Could not delete partner');
    } finally {
      setIsDeleting(false);
    }
  };

  const openEditModal = (partner: PartnerInfo) => {
    setFormData(partner);
    setSelectedPartnerId(getPartnerId(partner));
    setEditModalOpen(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Filter and paginate
  const filteredPartners = partners.filter(p => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      p.company_name?.toLowerCase().includes(q) ||
      p.first_name?.toLowerCase().includes(q) ||
      p.last_name?.toLowerCase().includes(q) ||
      p.email?.toLowerCase().includes(q)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filteredPartners.length / ROWS_PER_PAGE));
  const paginatedPartners = filteredPartners.slice(
    (currentPage - 1) * ROWS_PER_PAGE,
    currentPage * ROWS_PER_PAGE
  );

  return (
    <div className="w-full">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Partner Management</h1>
          <p className="text-sm text-slate-500 mt-1">
            {partners.length} authorized partner{partners.length !== 1 ? 's' : ''}
          </p>
        </div>
        <Button
          color="primary"
          className="bg-airlock-purple hover:bg-airlock-deep text-white font-medium"
          radius="sm"
          onClick={() => setAddPartnerModalOpen(true)}
        >
          + Add Partner
        </Button>
      </div>

      {error && (
        <Card className="mb-4 bg-red-50 border border-red-200 shadow-none">
          <CardBody className="p-3 flex flex-row items-center justify-between">
            <p className="text-sm text-red-700">{error}</p>
            <Button size="sm" variant="light" className="text-red-500" onClick={() => setError(null)}>
              Dismiss
            </Button>
          </CardBody>
        </Card>
      )}

      {/* Search */}
      <div className="mb-4">
        <Input
          placeholder="Search by name, company, or email..."
          variant="bordered"
          radius="sm"
          size="sm"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="max-w-sm"
          classNames={{ input: 'text-sm' }}
          startContent={
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-slate-400">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          }
          isClearable
          onClear={() => setSearchQuery('')}
        />
      </div>

      <Card className="bg-white border border-slate-200 shadow-none">
        <CardBody className="p-0">
          <PartnerTable
            partners={paginatedPartners}
            onEdit={openEditModal}
            onDelete={handleDeleteClick}
          />
        </CardBody>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-xs text-slate-400">
            Showing {(currentPage - 1) * ROWS_PER_PAGE + 1}–
            {Math.min(currentPage * ROWS_PER_PAGE, filteredPartners.length)} of{' '}
            {filteredPartners.length}
          </p>
          <Pagination
            total={totalPages}
            page={currentPage}
            onChange={setCurrentPage}
            size="sm"
            color="secondary"
            showControls
          />
        </div>
      )}

      <EditPartnerModal
        isOpen={isEditModalOpen}
        onClose={() => setEditModalOpen(false)}
        onSave={handleSaveClick}
        formData={formData}
        handleChange={handleChange}
        isLoading={isSaving}
      />
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        isLoading={isDeleting}
      />
      <AddPartnerModal
        isOpen={isAddPartnerModalOpen}
        onClose={() => setAddPartnerModalOpen(false)}
        createAuth0User={data => createAuth0User(data)}
        refreshPartners={fetchPartners}
      />
    </div>
  );
};

export default Admin;
